import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import LayoutWithNavbar from './LayoutWithNavbar';
import { fetchInvoiceDetailsByAuditLogId } from '../services/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const ITEMS_PER_PAGE = 30;

const InvoiceAuditLogDetailsPage = () => {
  const { auditLogId } = useParams();
  const [entries, setEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDetails = async () => {
      setLoading(true);
      const res = await fetchInvoiceDetailsByAuditLogId(auditLogId);
      if (res.success && Array.isArray(res.data)) {
        setEntries(res.data);
      } else {
        console.error("Invalid data format:", res.data);
        setEntries([]);
      }
      setLoading(false);
    };
    if (auditLogId) loadDetails();
  }, [auditLogId]);

  const filteredEntries = entries.filter(row => {
    const kw = searchTerm.toLowerCase();
    return (
      row.party?.toLowerCase().includes(kw) ||
      row.party_gstin?.toLowerCase().includes(kw) ||
      row.ref_no?.toLowerCase().includes(kw)
    );
  });

  const totalPages = Math.ceil(filteredEntries.length / ITEMS_PER_PAGE);
  const paginatedEntries = filteredEntries.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleExport = () => {
    if (!filteredEntries.length) return alert("No data to export");

    const exportData = filteredEntries.map((row, index) => ({
      "S.No": index + 1,
      "Party": row.party,
      "GSTIN": row.party_gstin,
      "Ref No": row.ref_no,
      "Ref Date": row.ref_date,
      "CGST": row.cgst,
      "SGST": row.sgst,
      "IGST": row.igst,
      "Total": row.total_value,
      "Net": row.net_amount,
      "Reconciled": row.eligible_in_form2b ? "Yes" : "No"
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `InvoiceBatch-${auditLogId}`);

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `Invoice_auditlog_Details.xlsx`);
  };

  return (
    <LayoutWithNavbar>
      <div style={styles.container}>
        <h2 style={styles.heading}>Invoice Audit Log Details</h2>

        <div style={styles.controls}>
          <input
            type="text"
            placeholder="Search GSTIN, Party or Ref No"
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // reset to first page on search
            }}
            style={styles.searchInput}
          />
          <button onClick={handleExport} style={styles.exportButton}>Export to Excel</button>
        </div>

        {loading ? (
          <p style={styles.loading}>Loading details...</p>
        ) : (
          <>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['S.No', 'Party', 'GSTIN', 'Ref No', 'Ref Date', 'CGST', 'SGST', 'IGST', 'Total', 'Net', 'Reconciled'].map((col, i) => (
                      <th key={i} style={styles.th}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedEntries.map((row, index) => (
                    <tr key={index} style={styles.tr}>
                      <td style={styles.td}>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                      <td style={styles.td}>{row.party}</td>
                      <td style={styles.td}>{row.party_gstin}</td>
                      <td style={styles.td}>{row.ref_no}</td>
                      <td style={styles.td}>{row.ref_date}</td>
                      <td style={styles.td}>{row.cgst}</td>
                      <td style={styles.td}>{row.sgst}</td>
                      <td style={styles.td}>{row.igst}</td>
                      <td style={styles.td}>{row.total_value}</td>
                      <td style={styles.td}>{row.net_amount}</td>
                      <td style={styles.td}>{row.eligible_in_form2b ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                  {!paginatedEntries.length && (
                    <tr>
                      <td colSpan={11} style={{ ...styles.td, textAlign: 'center', fontStyle: 'italic' }}>
                        No records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Match the reference pagination (Prev / Page X of Y / Next) */}
            <div style={styles.pagination}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ ...styles.navBtn, ...(currentPage === 1 ? styles.navBtnDisabled : {}) }}
              >
                ⟨ Prev
              </button>

              <span style={styles.pageInfo}>
                Page {totalPages ? currentPage : 0} of {totalPages}
              </span>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                style={{ ...styles.navBtn, ...(currentPage === totalPages || totalPages === 0 ? styles.navBtnDisabled : {}) }}
              >
                Next ⟩
              </button>
            </div>
          </>
        )}
      </div>
    </LayoutWithNavbar>
  );
};

const styles = {
  container: { padding: '30px', backgroundColor: '#f8f9fa', minHeight: '100vh' },
  heading: { fontSize: '24px', fontWeight: '600', marginBottom: '20px' },

  // align with reference page
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '20px',
    gap: '10px',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  searchInput: {
    padding: '10px',
    fontSize: '16px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    width: '35%',
    minWidth: '260px'
  },
  exportButton: {
    padding: '10px 20px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
  },

  loading: { fontSize: '18px', textAlign: 'center' },

  tableWrapper: {
    overflowX: 'auto',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 0 10px rgba(0,0,0,0.04)'
  },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '1200px' },
  th: {
    padding: '12px',
    backgroundColor: '#343a40',
    color: '#fff',
    fontWeight: '800',
    textAlign: 'left',
    borderBottom: '1px solid #dee2e6'
  },
  tr: { backgroundColor: '#fff' },
  td: {
    padding: '12px',
    borderBottom: '1px solid #dee2e6',
    fontSize: '15px'
  },

  // reference-style pagination
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '20px',
    gap: '12px'
  },
  pageInfo: { fontWeight: '600' },
  navBtn: {
    padding: '8px 14px',
    fontSize: '14px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    backgroundColor: '#fff',
    cursor: 'pointer'
  },
  navBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed'
  }
};

export default InvoiceAuditLogDetailsPage;
